import { Camera, LogOut, Save } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/db';
import { captureSilentError } from '../../services/posthog';
import { UsernameInput } from '../UsernameInput';

interface ProfileTabProps {
  shoutCount: number;
  onFriendsClick: () => void;
  onShoutsClick: () => void;
}

const ProfileTabComponent: React.FC<ProfileTabProps> = ({
  shoutCount,
  onFriendsClick,
  onShoutsClick
}) => {
  const { currentUser, updateProfile, logout } = useAuth();
  
  // Profile State
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Image Upload State
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Sync Profile Data
  useEffect(() => {
    if (currentUser) {
      setDisplayName(currentUser.displayName);
      setUsername(currentUser.username);
      setPhotoURL(currentUser.photoURL || '');
      setIsDirty(false);
    }
  }, [currentUser]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentUser) return;
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size must be less than 5MB");
        return;
    }

    if (!file.type.startsWith('image/')) {
        setUploadError("File must be an image");
        return;
    }

    try {
        setIsUploading(true);
        const url = await db.uploadProfileImage(currentUser.uid, file);
        setPhotoURL(url);
        setIsDirty(true);
    } catch (e) {
        captureSilentError("ProfileTab.handleImageUpload", e);
        setUploadError("Failed to upload image. Please try again.");
    } finally {
        setIsUploading(false);
    }
  };

  const handleSave = async () => {
      if (!currentUser) return;
      setIsSaving(true);
      setSaveError(null); // Clear previous errors
      try {
          await updateProfile({
              ...currentUser,
              displayName,
              photoURL,
              username
          });
          setIsDirty(false);
      } catch (e) {
          if ((e as any)?.code === '23505') {
              setSaveError("This username is already taken. Please try another one.");
          } else {
              setSaveError("Failed to update profile. Please try again.");
              captureSilentError('ProfileTab.handleSave', e);
          }
      } finally {
          setIsSaving(false);
      }
  };

  if (!currentUser) return null;

  return (
    <div className="p-6 space-y-6">
        <div className="flex flex-col items-center">
            <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleImageUpload} 
                className="hidden" 
                accept="image/*"
            />
            <div className="relative group cursor-pointer" onClick={() => !isUploading && fileInputRef.current?.click()}>
                <img 
                src={photoURL || "https://via.placeholder.com/150"} 
                alt="Profile" 
                className={`w-24 h-24 rounded-full object-cover border-4 border-brand-cream-dark shadow-sm ${isUploading ? 'opacity-50' : ''}`}
                />
                <div className={`absolute inset-0 bg-black/30 rounded-full flex items-center justify-center transition-opacity ${isUploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    {isUploading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    ) : (
                        <Camera className="text-white" size={24} />
                    )}
                </div>
            </div>
            {uploadError && <p className="text-xs text-brand-red font-bold mt-2">{uploadError}</p>}
        </div>

        <div className="space-y-4">
            <div>
                <label className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Display Name</label>
                <input 
                    type="text" 
                    value={displayName}
                    onChange={e => { setDisplayName(e.target.value); setIsDirty(true); }}
                    className="w-full p-3 bg-white border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red font-semibold text-brand-dark"
                />
            </div>
            <div>
                <label className="block text-xs font-bold text-brand-dark-light uppercase tracking-wider mb-1">Username</label>
                <div className="relative">
                    <UsernameInput 
                        value={username}
                        onChange={val => { setUsername(val); setIsDirty(true); }}
                        prefix="@"
                        className="p-3 bg-white border-2 border-brand-cream-dark rounded-xl focus-within:border-brand-red font-semibold text-brand-dark"
                    />
                </div>
            </div>
        </div>

        {/* Stats */}
        <div className="p-1 bg-white rounded-2xl flex justify-between items-stretch border border-brand-cream-dark">
            <button 
                onClick={onFriendsClick}
                className="text-center flex-1 border-r border-brand-cream-dark hover:bg-brand-cream/50 transition-colors active:scale-95 py-3 rounded-l-xl group"
            >
                <span className="block text-xl font-bold text-brand-dark group-hover:text-brand-red transition-colors">{currentUser.friends.length}</span>
                <span className="text-xs text-brand-dark-light uppercase font-bold tracking-wide">Friends</span>
            </button>
            <button 
                onClick={onShoutsClick}
                className="text-center flex-1 hover:bg-brand-cream/50 transition-colors active:scale-95 py-3 rounded-r-xl group"
            >
                <span className="block text-xl font-bold text-brand-dark group-hover:text-brand-red transition-colors">{shoutCount}</span>
                <span className="text-xs text-brand-dark-light uppercase font-bold tracking-wide">Shouts</span>
            </button>
        </div>


        {saveError && (
            <div className="p-3 mb-2 bg-red-50 border border-brand-red/20 rounded-xl text-brand-red text-xs font-bold text-center animate-in fade-in slide-in-from-top-1">
                {saveError}
            </div>
        )}

        <button 
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className="w-full py-3 bg-brand-red text-white rounded-xl font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-red-hover transition active:scale-95 flex items-center justify-center gap-2"
        >
            {isSaving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
                <>
                    <Save size={18} />
                    Save Changes
                </>
            )}
        </button>

        <button 
            className="w-full py-3 text-brand-red font-bold hover:bg-red-50 rounded-xl transition flex items-center justify-center gap-2"
            onClick={logout}
        >
            <LogOut size={18} />
            Sign Out
        </button>
    </div>
  );
};

export const ProfileTab = React.memo(ProfileTabComponent);
